// Inventory management hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-client';
import type { Inventory } from '@/types/entities';
import type { MeasurementUnit } from '@/types/api.types';
import { isAssistantLoggedIn } from '@/lib/permissions';

/**
 * Fetch all inventory items for a specific restaurant
 * Supports both admin users and assistants with appropriate permissions
 */
export function useInventory(restaurantId: string | null) {
  return useQuery({
    queryKey: queryKeys.inventory.list({ restaurantId }),
    staleTime: 2 * 60 * 1000, // 2 minutes - inventory changes more frequently
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false, // Inventory updates via mutations
    refetchOnMount: false, // Don't refetch if data is fresh
    queryFn: async (): Promise<Inventory[]> => {
      if (!restaurantId) {
        return [];
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        // Use assistant RPC function
        const { data, error } = await supabase.rpc('assistant_get_inventory', {
          p_restaurant_id: restaurantId,
        });

        if (error) {
          // If permission denied, return empty array instead of throwing
          if (error.message?.includes('PERMISSION_DENIED')) {
            console.warn('Assistant does not have permission to view inventory');
            return [];
          }
          throw error;
        }

        // For assistant query, we need to fetch ingredients separately using RPC
        // Assistants don't have direct table access, so we use the RPC function
        const { data: ingredients, error: ingredientsError } = await supabase.rpc('assistant_get_ingredients', {
          p_restaurant_id: restaurantId,
        });

        if (ingredientsError) {
          console.warn('Failed to fetch ingredients for assistant:', ingredientsError);
        }

        const ingredientsMap = new Map((ingredients || []).map((ing: any) => [ing.id, ing]));

        return (data || []).map((i: any) => {
          const ingredient = ingredientsMap.get(i.ingredient_id);
          const base: Inventory = {
            id: i.id,
            restaurantId: i.restaurant_id,
            ingredientId: i.ingredient_id,
            quantity: i.quantity,
            unit: i.unit as MeasurementUnit,
            minThreshold: i.min_threshold || 0,
            lastRestocked: i.last_restocked || null,
            averageCost: i.average_cost || 0,
            totalValue: i.total_value || 0,
            createdAt: i.created_at || '',
            updatedAt: i.updated_at || '',
          };
          if (ingredient) {
            base.ingredient = {
              id: ingredient.id,
              restaurantId: ingredient.restaurant_id,
              nameAr: ingredient.name_ar,
              nameFr: ingredient.name_fr,
              emoji: ingredient.emoji || '',
              createdAt: ingredient.created_at || '',
              updatedAt: ingredient.updated_at || '',
            };
          }
          return base;
        });
      }

      // Admin user - use direct query
      const { data, error } = await supabase
        .from('inventory')
        .select(
          `
          *,
          ingredient:ingredients(*)
        `
        )
        .eq('restaurant_id', restaurantId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return (data || []).map((i) => {
        const base: Inventory = {
          id: i.id,
          restaurantId: i.restaurant_id,
          ingredientId: i.ingredient_id,
          quantity: i.quantity,
          unit: i.unit as MeasurementUnit,
          minThreshold: i.min_threshold || 0,
          lastRestocked: i.last_restocked || null,
          averageCost: (i as any).average_cost || 0,
          totalValue: (i as any).total_value || 0,
          createdAt: i.created_at || '',
          updatedAt: i.updated_at || '',
        };
        if (i.ingredient) {
          base.ingredient = {
            id: i.ingredient.id,
            restaurantId: i.ingredient.restaurant_id,
            nameAr: i.ingredient.name_ar,
            nameFr: i.ingredient.name_fr,
            emoji: i.ingredient.emoji || '',
            createdAt: i.ingredient.created_at || '',
            updatedAt: i.ingredient.updated_at || '',
          };
        }
        return base;
      });
    },
    enabled: !!restaurantId,
  });
}

/**
 * Fetch single inventory item by ID
 */
export function useInventoryItem(inventoryId: string) {
  return useQuery({
    queryKey: queryKeys.inventory.detail(inventoryId),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<Inventory> => {
      const { data, error } = await supabase
        .from('inventory')
        .select('*')
        .eq('id', inventoryId)
        .single();

      if (error) {
        throw error;
      }

      return {
        id: data.id,
        restaurantId: data.restaurant_id,
        ingredientId: data.ingredient_id,
        quantity: data.quantity,
        unit: data.unit as MeasurementUnit,
        minThreshold: data.min_threshold || 0,
        lastRestocked: data.last_restocked || null,
        averageCost: (data as any).average_cost || 0,
        totalValue: (data as any).total_value || 0,
        createdAt: data.created_at || '',
        updatedAt: data.updated_at || '',
      };
    },
    enabled: !!inventoryId,
  });
}

/**
 * Create or update inventory for an ingredient
 * Supports both admin users and assistants with appropriate permissions
 */
export function useUpsertInventory(restaurantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      ingredientId: string;
      quantity: number;
      unit: MeasurementUnit;
      minThreshold: number;
    }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        // RPC function added by migration 20251211000001
        const { data, error } = await (supabase.rpc as any)('assistant_upsert_inventory', {
          p_restaurant_id: restaurantId,
          p_ingredient_id: request.ingredientId,
          p_quantity: request.quantity,
          p_unit: request.unit,
          p_min_threshold: request.minThreshold,
        });

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied');
          }
          throw error;
        }
        return data;
      }

      // Admin user - use direct query
      // First check if inventory already exists for this ingredient
      const { data: existing } = await supabase
        .from('inventory')
        .select('id')
        .eq('restaurant_id', restaurantId)
        .eq('ingredient_id', request.ingredientId)
        .single();

      if (existing) {
        // Update existing inventory
        const { data, error } = await supabase
          .from('inventory')
          .update({
            quantity: request.quantity,
            unit: request.unit,
            min_threshold: request.minThreshold,
            last_restocked: new Date().toISOString(),
          })
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;
        return data.id;
      } else {
        // Create new inventory
        const { data, error } = await supabase
          .from('inventory')
          .insert({
            restaurant_id: restaurantId,
            ingredient_id: request.ingredientId,
            quantity: request.quantity,
            unit: request.unit,
            min_threshold: request.minThreshold,
            last_restocked: new Date().toISOString(),
          })
          .select()
          .single();

        if (error) throw error;
        return data.id;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inventory.list({ restaurantId }),
      });
    },
  });
}

/**
 * Update inventory quantity
 * Supports both admin users and assistants with appropriate permissions
 */
export function useUpdateInventoryQuantity(restaurantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { inventoryId: string; quantity: number }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        // RPC function added by migration 20251211000001
        const { data, error } = await (supabase.rpc as any)('assistant_update_inventory_quantity', {
          p_restaurant_id: restaurantId,
          p_inventory_id: request.inventoryId,
          p_quantity: request.quantity,
        });

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied');
          }
          throw error;
        }
        return data;
      }

      // Admin user - use direct query
      const { data, error } = await supabase
        .from('inventory')
        .update({
          quantity: request.quantity,
          last_restocked: new Date().toISOString(),
        })
        .eq('id', request.inventoryId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inventory.list({ restaurantId }),
      });
    },
  });
}

/**
 * Update inventory min threshold only (by inventory ID)
 * Supports both admin users and assistants with appropriate permissions
 */
export function useUpdateMinThreshold(restaurantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { inventoryId: string; minThreshold: number }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        // RPC function added by migration 20251211000001
        const { data, error } = await (supabase.rpc as any)('assistant_update_inventory_threshold', {
          p_restaurant_id: restaurantId,
          p_inventory_id: request.inventoryId,
          p_min_threshold: request.minThreshold,
        });

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied');
          }
          throw error;
        }
        return data;
      }

      // Admin user - use direct query
      const { data, error } = await supabase
        .from('inventory')
        .update({
          min_threshold: request.minThreshold,
        })
        .eq('id', request.inventoryId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inventory.list({ restaurantId }),
      });
    },
  });
}

/**
 * Update inventory min threshold by ingredient ID
 * Supports both admin users and assistants with appropriate permissions
 */
export function useUpdateMinThresholdByIngredient(restaurantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { ingredientId: string; minThreshold: number }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        // RPC function added by migration 20251211000001
        const { data, error } = await (supabase.rpc as any)('assistant_update_inventory_threshold_by_ingredient', {
          p_restaurant_id: restaurantId,
          p_ingredient_id: request.ingredientId,
          p_min_threshold: request.minThreshold,
        });

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied');
          }
          throw error;
        }
        return data;
      }

      // Admin user - use direct query
      const { data, error } = await supabase
        .from('inventory')
        .update({
          min_threshold: request.minThreshold,
        })
        .eq('restaurant_id', restaurantId)
        .eq('ingredient_id', request.ingredientId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inventory.list({ restaurantId }),
      });
    },
  });
}

/**
 * Delete inventory item
 * Supports both admin users and assistants with appropriate permissions
 */
export function useDeleteInventory(restaurantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inventoryId: string) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        // RPC function added by migration 20251211000001
        const { error } = await (supabase.rpc as any)('assistant_delete_inventory', {
          p_restaurant_id: restaurantId,
          p_inventory_id: inventoryId,
        });

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied');
          }
          throw error;
        }
        return;
      }

      // Admin user - use direct query
      const { error } = await supabase.from('inventory').delete().eq('id', inventoryId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.inventory.list({ restaurantId }),
      });
    },
  });
}
